import * as React from 'react';
import { ISubentity, ISuggestedSubentity, isLinkedSubentity } from "../../../entities/Subentities";
import { ItemCreation, ICommand } from '../ItemCreation';
import { Validator } from "../../../validation";
import * as Metadata from '../../../entities/Metadata';
import { DisplayFieldService } from '../../common/DisplayFieldService';
import { OnChangeEvent } from "../sectionsControl/uiControls/fieldsArea/FieldComponent";
import { filterUnique, notUndefined } from '../../utils/common';
import { Dictionary } from '../../../entities/common';
import FieldsEdit from './FieldsEdit';
import { IExtraTab } from '../ExtraTabs/ExtraTabs';
import HeaderText from './HeaderText';
import { FieldConfig } from './FieldEditor';
import { ViewService } from '../../../services/ViewService';
import { GenericPanelProps } from './UpdatePanel';
import SuggestedReason from '../SuggestedReason';

const EmptyTabArray: IExtraTab[] = [];

export type OnComplete = (id: string, changes: Dictionary<unknown>) => void;

type Props<TSubentity extends ISubentity & ISuggestedSubentity> = GenericPanelProps<TSubentity> & {
    onConfirmSuggested: (item: TSubentity) => void;
    onRejectSuggested: (item: TSubentity) => void;
    entity: TSubentity;
}

export const SuggestedConfirmPanel = <TSubentity extends ISubentity & ISuggestedSubentity>(props: Props<TSubentity>) => {
    const { subentityType, entity, onDismiss = EmptyTabArray, children, operation,
        uiControlElementsCustomRender, customFieldValidatorBuilder, editableNativeFieldsForLinkedEntities,
        theOnlyEditableFieldsForLinkedEntities, readOnlyFields, customCommands } = props;

    const mandatoryEditableFields = React.useMemo(() => props.mandatoryEditableFields?.(entity) ?? [], [entity, props.mandatoryEditableFields]);
    const readOnly = !!props.readOnly && !mandatoryEditableFields.length;

    const [changes, setChanges] = React.useState<Dictionary<unknown>>({});

    // full entity necessary for:
    // predecessor field type to filter entity from suggestion list
    // to correctly render entity changes in extraTabs because they can use some entity specific data
    const editEntity = React.useMemo(() => ({ ...entity, attributes: { ...entity.attributes, ...changes } }), [changes, entity]);

    const isFieldReadOnly = (field: Metadata.Field): boolean => {
        return props.readOnly || field.isReadonly
            || ViewService.isReadonlyField(field, readOnlyFields, subentityType, entity, editableNativeFieldsForLinkedEntities, theOnlyEditableFieldsForLinkedEntities);
    }

    const fields = props.fields instanceof Function ? props.fields(editEntity) : props.fields;
    const config: Dictionary<FieldConfig> = React.useMemo(
        () => fields
            .map(_ => ({
                fieldName: _.name,
                renderInput: uiControlElementsCustomRender?.[_.name],
                validator: !mandatoryEditableFields.includes(_.name) && isFieldReadOnly(_) ? undefined :
                    customFieldValidatorBuilder?.[_.name]?.(editEntity as TSubentity, _) || DisplayFieldService.buildValidator(_),
                readOnly: !mandatoryEditableFields.includes(_.name) && isFieldReadOnly(_)
            }))
            .reduce((p, c) => ({ ...p, [c.fieldName]: c }), {}),
        [fields, editEntity, mandatoryEditableFields]);

    const displayFields: string[] = React.useMemo(
        () => filterUnique((props.mandatoryEditFields || []).concat(props.displayFields)),
        [props.displayFields, props.mandatoryEditFields]);

    const isValid = React.useMemo(
        () => {
            const validators = fields
                .map(_ => _.name)
                .filter(_ => config[_]?.validator)
                .reduce((v, k) => ({ ...v, [k]: config[k].validator }), {});
            return Validator.isValid(validators, editEntity.attributes);
        },
        [config, editEntity, fields]);

    const subentityTypeLabel = props.subentityTypeLabel instanceof Function ? props.subentityTypeLabel(entity) : props.subentityTypeLabel;
    const className = props.className instanceof Function ? props.className(entity) : props.className;

    const [isDirty, setIsDirty] = React.useState(props.isDirty && isValid);
    const setDirty = React.useCallback(() => setIsDirty(true), []);
    const onNameChanged = React.useCallback((newName: string) => {
        setChanges((p) => ({ ...p, "Name": newName }));
        setDirty();
    }, []);
    const onChanged = React.useCallback<OnChangeEvent>(
        (f, v, extra) => {
            setChanges((p) => ({ ...p, [f]: v, ...extra }));
            setDirty();
        },
        [changes]);

    const commands = React.useMemo(() => {
        return [
            !readOnly ? {
                primary: true,
                iconName: 'Accept',
                title: `Accept`,
                text: `Accept`,
                onClick: () => props.onConfirmSuggested((editEntity)),
                disabled: !isValid
            } as ICommand : undefined,
            !readOnly ? {
                title: `Reject`,
                text: `Reject`,
                iconName: 'Broom',
                onClick: () => props.onRejectSuggested((editEntity))
            } as ICommand : undefined,
            {
                text: 'Close',
                onClick: onDismiss
            } as ICommand,
        ]
            .filter(notUndefined)
            .concat(customCommands ?? []);

    }, [entity.id, changes, onDismiss, readOnly, subentityTypeLabel, isValid, isDirty, customCommands]);

    return <ItemCreation onDismiss={props.onDismiss}
        header={{
            showNameEditor: true,
            nameEditorLabel: `Suggested ${subentityTypeLabel} Name`,
            disableNameEditor: readOnly || isLinkedSubentity(subentityType, entity),
            onChanged: onNameChanged,
            validator: config["Name"]?.validator,
            value: editEntity.attributes['Name'],
            text: <HeaderText {...props} operation={operation} readOnly={readOnly} subentityTypeLabel={`Suggested ${subentityTypeLabel}`} suffixRender={props.header?.suffixRender} />,
            ...props.header,
        }}
        isDirty={isDirty}
        commands={commands}
        dirtyMessage={`You have unsaved changes. Click Accept to save the changes and convert this suggested ${subentityTypeLabel} into a regular one. Click Reject to delete the suggested ${subentityTypeLabel}. Click Close to exit without saving the changes.`}
        className={`subentity-panel ${className}`}>
        <div className="panel-area">
            {entity.suggestionReason && <div className="grid-item"><SuggestedReason entity={editEntity} /></div>}
            <FieldsEdit
                fields={fields}
                entity={editEntity}
                config={config}
                displayFields={displayFields}
                subentityType={subentityType}
                onChanged={onChanged}
            />
        </div>
        {children}
    </ItemCreation>;
}
